import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Page from "@/components/Page";
import StepProgress from "@/components/StepProgress";
import VoiceTextarea from "@/components/VoiceTextarea";
import VoiceInput from "@/components/VoiceInput";
import FileUploader from "@/components/FileUploader";
import { api, getReturnToken, setReturnToken, clearReturnToken } from "@/lib/api";
import { APPLY } from "@/constants/testIds";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Save, Sparkles, X, Plus, Trash2, Wand2 } from "lucide-react";

const STEPS = [
  "Contact",
  "Current Housing",
  "Income",
  "References",
  "Your Story",
  "Lease",
  "Review & Sign",
];

const STORY_QUESTIONS = [
  "Tell us a little about yourself and what brings you to this point in your life.",
  "What would having stable housing make possible for you or your household?",
  "What are you currently doing to create greater stability in your life?",
  "What is your plan for consistently paying rent each month?",
  "What routines or plans will help you maintain and care for the apartment?",
  "How do you normally handle problems or disagreements with a landlord or neighbor?",
  "What have you learned from previous housing experiences?",
  "What strengths do you feel you possess in life?",
  "Is there anything about your current circumstances that you would like us to understand?",
  "What would success look like for you six months to one year from now?",
  "Is there anything else you would like to tell us?",
];

// Supportive helper text shown under specific questions (by index).
const STORY_HELP = {
  7: "This can include personal strengths, survival skills, lessons you have learned, values you live by, support systems, work ethic, parenting strengths if you choose to share that, faith if you choose to share that, creativity, determination, or anything else you feel proud of. We are not looking for perfection. We are looking to understand who you are.",
  9: "You can talk about housing, family, work, school, healing, stability, finances, relationships, recovery, personal growth, or any goals that matter to you. This question is not used to judge you. It helps us understand what you are hoping to build.",
};

const LEASE_ITEMS = [
  ["rent", "Rent must be paid according to the lease."],
  ["clean", "The apartment must be kept reasonably clean and maintained."],
  ["damage", "Damage beyond ordinary wear and tear may be charged to the tenant."],
  ["guests", "Tenants are responsible for the behavior of their guests."],
  ["unauthorized", "Unauthorized occupants are not permitted."],
  ["illegal", "Illegal activity, violence, threats, serious disturbances, and major lease violations will be addressed according to the lease and Nebraska law."],
  ["maintenance", "Maintenance concerns should be reported promptly."],
  ["communication", "Honest and timely communication is expected."],
  ["mtm", "The initial rental agreement will be month-to-month."],
  ["law", "All lease enforcement and termination procedures will follow applicable law."],
];

const ENCOURAGE = [
  "Take your time. You can save and come back later.",
  "We are looking for honesty, not perfection.",
  "You are more than your paperwork.",
  "Just a few more details to help us understand your journey.",
  "You're doing great. A real person will read this.",
  "Your story belongs to you. Voluntary questions may be skipped.",
  "Please review your answers before continuing.",
  "Thank you for taking the time to share with us.",
];

export default function Apply() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => {
    // Cross-device resume: /apply/return?token=... from an emailed link wins
    // over any token saved on this device.
    const urlToken = new URLSearchParams(window.location.search).get("token");
    if (urlToken) {
      setReturnToken(urlToken);
      return urlToken;
    }
    return getReturnToken();
  });
  const [appDoc, setAppDoc] = useState(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Bootstrap: create or load
  useEffect(() => {
    (async () => {
      try {
        let t = token;
        if (!t) {
          const { data } = await api.post("/applications", {});
          setReturnToken(data.return_token);
          t = data.return_token;
          setToken(t);
        }
        const { data: doc } = await api.get(`/applications/${t}`);
        setAppDoc(doc);
        setStep(doc.current_step || 1);
      } catch (e) {
        clearReturnToken();
        toast.error("Could not load application. Starting a new one.");
        try {
          const { data } = await api.post("/applications", {});
          setReturnToken(data.return_token);
          setToken(data.return_token);
          const { data: doc } = await api.get(`/applications/${data.return_token}`);
          setAppDoc(doc);
          setStep(1);
        } catch (err) {
          toast.error("Could not start application: " + err.message);
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const data = appDoc?.data || {};
  const setSection = (key, val) => {
    setAppDoc((d) => ({ ...d, data: { ...d.data, [key]: val } }));
  };
  const patch = (section, key, val) => {
    setAppDoc((d) => ({ ...d, data: { ...d.data, [section]: { ...d.data[section], [key]: val } } }));
  };

  const saveProgress = useCallback(async (silent = false) => {
    if (!token || !appDoc) return;
    setSaving(true);
    try {
      await api.patch(`/applications/${token}`, { data: appDoc.data, current_step: step });
      if (!silent) toast.success("Saved");
    } catch (e) {
      toast.error("Could not save: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }, [token, appDoc, step]);

  // Autosave every 25s
  useEffect(() => {
    if (!appDoc || appDoc.status === "submitted") return;
    const id = setInterval(() => saveProgress(true), 25_000);
    return () => clearInterval(id);
  }, [appDoc, saveProgress]);

  const next = async () => {
    await saveProgress(true);
    setStep((s) => Math.min(STEPS.length, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    await saveProgress(true);
    try {
      const { data: res } = await api.post(`/applications/${token}/submit`);
      toast.success("Application submitted!");
      navigate(`/submitted/${res.confirmation_number}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing) {
        toast.error("Please complete: " + detail.missing.join(", "));
      } else {
        toast.error("Could not submit: " + (typeof detail === "string" ? detail : e.message));
      }
    }
  };

  if (loading || !appDoc) {
    return <Page><div className="max-w-3xl mx-auto p-10 text-center" style={{ color: "#475569" }}>Loading your application…</div></Page>;
  }

  const submitted = appDoc.status === "submitted";

  return (
    <Page>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <StepProgress step={step} total={STEPS.length} labels={STEPS} testId={APPLY.progressBar} />
        <div className="encourage mt-5 text-sm">{ENCOURAGE[(step - 1) % ENCOURAGE.length]}</div>

        <div className="p2p-card p-6 sm:p-10 mt-6">
          {submitted ? (
            <div className="text-center py-10">
              <h2 className="font-serif text-2xl" style={{ color: "#0B2D59" }}>This application is already submitted.</h2>
              <p className="mt-2" style={{ color: "#475569" }}>Confirmation #: {appDoc.confirmation_number}</p>
              <button className="p2p-btn-primary mt-4" onClick={() => navigate(`/submitted/${appDoc.confirmation_number}`)}>View receipt</button>
            </div>
          ) : (
            <>
              {step === 1 && <ContactStep data={data.contact || {}} occupants={data.occupants || []}
                onPatch={(k, v) => patch("contact", k, v)}
                onOccupants={(v) => setSection("occupants", v)} />}
              {step === 2 && <HousingStep data={data.housing || {}} token={token}
                onPatch={(k, v) => patch("housing", k, v)} />}
              {step === 3 && <IncomeStep data={data.income || {}} documents={(appDoc.documents || []).filter((d) => !d.is_deleted)}
                token={token}
                onPatch={(k, v) => patch("income", k, v)}
                onUploaded={(f) => setAppDoc((d) => ({ ...d, documents: [...(d.documents || []), f] }))}
                onReplaced={(oldId, newFile) => setAppDoc((d) => ({
                  ...d,
                  documents: [...(d.documents || []).map((x) => x.id === oldId ? { ...x, is_deleted: true } : x), newFile],
                }))}
                onRemoved={(docId) => setAppDoc((d) => ({
                  ...d,
                  documents: (d.documents || []).map((x) => x.id === docId ? { ...x, is_deleted: true } : x),
                }))} />}
              {step === 4 && <ReferencesStep refs={data.references || []}
                onChange={(v) => setSection("references", v)} />}
              {step === 5 && <StoryStep story={data.story || {}} token={token}
                onPatch={(k, v) => patch("story", k, v)} />}
              {step === 6 && <LeaseStep ack={data.lease_ack || {}}
                onPatch={(k, v) => patch("lease_ack", k, v)} />}
              {step === 7 && <ReviewStep doc={appDoc} signature={data.signature || {}}
                onPatch={(k, v) => patch("signature", k, v)}
                onJump={(s) => setStep(s)} />}
            </>
          )}
        </div>

        {!submitted && (
          <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="flex gap-3">
              <button className="p2p-btn-outline inline-flex items-center gap-2" disabled={step === 1}
                onClick={prev} data-testid={APPLY.prevBtn}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button className="p2p-btn-secondary inline-flex items-center gap-2"
                onClick={() => saveProgress(false)} disabled={saving} data-testid={APPLY.saveBtn}>
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
            <div className="flex gap-3">
              {step < STEPS.length && (
                <button className="p2p-btn-primary inline-flex items-center gap-2" onClick={next} data-testid={APPLY.nextBtn}>
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              )}
              {step === STEPS.length && (
                <button className="p2p-btn-primary inline-flex items-center gap-2" onClick={submit} data-testid={APPLY.submitBtn}>
                  Submit application
                </button>
              )}
            </div>
          </div>
        )}

        <p className="mt-6 text-xs text-center" style={{ color: "#475569" }}>
          Your progress is saved automatically. You can leave and come back to this device anytime.
        </p>
      </div>
    </Page>
  );
}

// ---------------- Step components ----------------
// Money entry without the browser number spinner: text field + decimal
// keyboard on mobile. Applicant types the amount; light validation on blur.
function MoneyInput({ value, onChange, testId, placeholder }) {
  const [err, setErr] = useState("");
  const handle = (e) => {
    const v = e.target.value.replace(/[$,\s]/g, "");
    if (v === "" || /^\d{0,7}(\.\d{0,2})?$/.test(v)) {
      onChange(v);
      if (err) setErr("");
    }
  };
  const validate = () => {
    if (!value) { setErr(""); return; }
    const n = parseFloat(value);
    if (isNaN(n) || n <= 0 || n > 999999) {
      setErr("Please enter a valid amount, like 18.50 or 2400.");
    } else {
      setErr("");
    }
  };
  return (
    <div>
      <input type="text" inputMode="decimal" autoComplete="off" className="p2p-input"
        data-testid={testId} placeholder={placeholder}
        value={value} onChange={handle} onBlur={validate} />
      {err && (
        <div className="text-xs mt-1" data-testid={testId ? `${testId}-error` : undefined} style={{ color: "#B91C1C" }}>
          {err}
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children, required }) {
  return (
    <label className="block">
      <span className="p2p-label">{label}{required && <span style={{ color: "#B91C1C" }}> *</span>}</span>
      {children}
      {hint && <span className="p2p-help block mt-1">{hint}</span>}
    </label>
  );
}

function ContactStep({ data, occupants, onPatch, onOccupants }) {
  const addOcc = () => onOccupants([...(occupants || []), { name: "", is_adult: false, phone: "", email: "" }]);
  const setOcc = (i, k, v) => onOccupants(occupants.map((o, idx) => idx === i ? { ...o, [k]: v } : o));
  const rmOcc = (i) => onOccupants(occupants.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-5">
      <SectionHeader title="Step 1 — Contact"
        sub="Let us know how to reach you. We will use this information only to communicate about your application." />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Legal name" required>
          <VoiceInput testId={APPLY.legalName} micTestId="apply-legal-name-mic"
            value={data.legal_name || ""} onChange={(v) => onPatch("legal_name", v)} />
        </Field>
        <Field label="Preferred name">
          <VoiceInput testId={APPLY.preferredName} micTestId="apply-preferred-name-mic"
            value={data.preferred_name || ""} onChange={(v) => onPatch("preferred_name", v)} />
        </Field>
        <Field label="Phone number" required>
          <input className="p2p-input" data-testid={APPLY.phone}
            value={data.phone || ""} onChange={(e) => onPatch("phone", e.target.value)} />
        </Field>
        <Field label="Email address" required>
          <input type="email" className="p2p-input" data-testid={APPLY.email}
            value={data.email || ""} onChange={(e) => onPatch("email", e.target.value)} />
        </Field>
      </div>
      <Field label="Current address" required>
        <VoiceInput testId={APPLY.currentAddress} micTestId="apply-current-address-mic"
          value={data.current_address || ""} onChange={(v) => onPatch("current_address", v)} />
      </Field>
      <Field label="Preferred method of contact" required>
        <select className="p2p-input" data-testid={APPLY.contactPreference}
          value={data.contact_preference || ""} onChange={(e) => onPatch("contact_preference", e.target.value)}>
          <option value="">Select…</option>
          <option value="call">Call</option>
          <option value="text">Text</option>
          <option value="email">Email</option>
        </select>
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Desired move-in date" required>
          <input type="date" className="p2p-input" data-testid={APPLY.moveInDate}
            value={data.desired_move_in_date || ""} onChange={(e) => onPatch("desired_move_in_date", e.target.value)} />
        </Field>
        <Field label="Number of people who would live here" required>
          <input type="number" min="1" max="6" className="p2p-input" data-testid={APPLY.occupantsCount}
            value={data.occupants_count || ""} onChange={(e) => onPatch("occupants_count", e.target.value)} />
        </Field>
      </div>
      <label className="flex items-start gap-3">
        <input type="checkbox" className="mt-1" data-testid={APPLY.isAdult}
          checked={!!data.is_adult} onChange={(e) => onPatch("is_adult", e.target.checked)} />
        <span className="text-sm" style={{ color: "#0B2D59" }}>I confirm I am at least 18 years old.</span>
      </label>

      <div>
        <div className="font-serif text-lg mb-2" style={{ color: "#0B2D59" }}>Other adults who would live here</div>
        <p className="p2p-help mb-3">If anyone else 18+ would live with you, list them here. We do not ask about minors.</p>
        <div className="space-y-3">
          {(occupants || []).map((o, i) => (
            <div key={i} className="p-4 rounded-xl" style={{ background: "#F4F8FB", border: "1px solid #E2E8F0" }}>
              <div className="grid sm:grid-cols-2 gap-3">
                <VoiceInput placeholder="Full name" value={o.name || ""} onChange={(v) => setOcc(i, "name", v)} testId={`apply-occ-name-${i}`} micTestId={`apply-occ-name-mic-${i}`} />
                <input className="p2p-input" placeholder="Phone" value={o.phone || ""} onChange={(e) => setOcc(i, "phone", e.target.value)} />
                <input className="p2p-input" placeholder="Email" value={o.email || ""} onChange={(e) => setOcc(i, "email", e.target.value)} />
                <label className="flex items-center gap-2 text-sm" style={{ color: "#0B2D59" }}>
                  <input type="checkbox" checked={!!o.is_adult} onChange={(e) => setOcc(i, "is_adult", e.target.checked)} />
                  Confirm 18 or older
                </label>
              </div>
              <button className="text-sm mt-2 inline-flex items-center gap-1" style={{ color: "#B91C1C" }} onClick={() => rmOcc(i)}>
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
          ))}
          <button type="button" className="p2p-btn-outline inline-flex items-center gap-2" data-testid={APPLY.addOccupant} onClick={addOcc}>
            <Plus className="w-4 h-4" /> Add another adult occupant
          </button>
        </div>
      </div>
    </div>
  );
}

function HousingStep({ data, token, onPatch }) {
  return (
    <div className="space-y-5">
      <SectionHeader title="Step 2 — Current housing"
        sub="Everyone's housing journey is different. Share what you are comfortable sharing about where you are now." />
      <Field label="Where are you currently living?">
        <select className="p2p-input" data-testid={APPLY.housingType}
          value={data.housing_type || ""} onChange={(e) => onPatch("housing_type", e.target.value)}>
          <option value="">Select…</option>
          {["Renting", "Staying with family or friends", "Temporary housing", "Shelter or transitional housing",
            "Hotel", "No stable housing", "Other", "Prefer not to answer"].map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </Field>
      <Field label="How long have you lived there?">
        <VoiceInput testId={APPLY.housingDuration} micTestId="apply-housing-duration-mic"
          value={data.duration || ""} onChange={(v) => onPatch("duration", v)}
          placeholder="e.g., 2 years and 3 months" />
      </Field>
      <Field label="Why are you looking for a new place to live?">
        <VoiceTextarea
          testId={APPLY.reasonMoving} micTestId="apply-reason-moving-mic"
          value={data.reason_moving || ""} onChange={(v) => onPatch("reason_moving", v)}
          placeholder="Share whatever feels comfortable."
          rows={3} maxChars={1500}
        />
      </Field>
      <Field label="What date do you need housing?">
        <input type="date" className="p2p-input" data-testid={APPLY.needHousingDate}
          value={data.need_date || ""} onChange={(e) => onPatch("need_date", e.target.value)} />
      </Field>
      <Field label="Have you previously rented a home or apartment?">
        <select className="p2p-input" data-testid={APPLY.rentedBefore}
          value={data.rented_before || ""} onChange={(e) => onPatch("rented_before", e.target.value)}>
          <option value="">Select…</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
          <option value="prefer_not">Prefer not to answer</option>
        </select>
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Current or previous landlord name (if available)">
          <VoiceInput testId={APPLY.landlordName} micTestId="apply-landlord-name-mic"
            value={data.landlord_name || ""} onChange={(v) => onPatch("landlord_name", v)} />
        </Field>
        <Field label="Landlord contact (phone or email)">
          <input className="p2p-input" data-testid={APPLY.landlordContact}
            value={data.landlord_contact || ""} onChange={(e) => onPatch("landlord_contact", e.target.value)} />
        </Field>
      </div>
      <Field label="Anything about your rental history you'd like to explain? (Optional)"
        hint="You can type or use the microphone. Voluntary.">
        <VoiceTextarea
          value={data.rental_explain || ""} onChange={(v) => onPatch("rental_explain", v)}
          placeholder="Optional. Share only what you're comfortable sharing."
          testId={APPLY.rentalExplain} micTestId="apply-rental-explain-mic"
          rows={4}
        />
      </Field>
    </div>
  );
}

function IncomeStep({ data, documents, token, onPatch, onUploaded, onReplaced, onRemoved }) {
  return (
    <div className="space-y-5">
      <SectionHeader title="Step 3 — Income & ability to pay"
        sub="We accept different lawful ways to show ability to pay rent, including employment, benefits, housing assistance, agency support, savings, or third-party support. No credit check." />
      <Field label="Current employment status">
        <select className="p2p-input" data-testid={APPLY.employmentStatus}
          value={data.employment_status || ""} onChange={(e) => onPatch("employment_status", e.target.value)}>
          <option value="">Select…</option>
          {["Employed full-time", "Employed part-time", "Self-employed", "Receiving lawful benefits or subsidies",
            "Student with income", "Between jobs", "Other"].map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Employer or business name"><VoiceInput testId={APPLY.employer} micTestId="apply-employer-mic"
          value={data.employer || ""} onChange={(v) => onPatch("employer", v)} /></Field>
        <Field label="Job title"><VoiceInput testId={APPLY.jobTitle} micTestId="apply-job-title-mic"
          value={data.job_title || ""} onChange={(v) => onPatch("job_title", v)} /></Field>
        <Field label="Employer address"><VoiceInput testId={APPLY.employerAddress} micTestId="apply-employer-address-mic"
          value={data.employer_address || ""} onChange={(v) => onPatch("employer_address", v)} /></Field>
        <Field label="Supervisor / verification contact"><VoiceInput testId={APPLY.supervisor} micTestId="apply-supervisor-mic"
          value={data.supervisor || ""} onChange={(v) => onPatch("supervisor", v)} /></Field>
        <Field label="Employer phone"><input className="p2p-input" data-testid={APPLY.employerPhone}
          value={data.employer_phone || ""} onChange={(e) => onPatch("employer_phone", e.target.value)} /></Field>
        <Field label="Length of employment"><VoiceInput testId={APPLY.lengthEmployment} micTestId="apply-length-employment-mic"
          value={data.length_employment || ""} onChange={(v) => onPatch("length_employment", v)}
          placeholder="e.g., one year and four months" /></Field>
        <Field label="Hourly wage or pay amount ($)">
          <MoneyInput testId={APPLY.grossMonthlyIncome} placeholder="Example: 18.50"
            value={data.gross_monthly_income || ""} onChange={(v) => onPatch("gross_monthly_income", v)} />
        </Field>
        <Field label="Pay frequency">
          <select className="p2p-input" data-testid={APPLY.payFrequency}
            value={data.pay_frequency || ""} onChange={(e) => onPatch("pay_frequency", e.target.value)}>
            <option value="">Select…</option>
            {["Weekly", "Bi-weekly", "Twice a month", "Monthly", "Other"].map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Other lawful and verifiable sources of income">
        <VoiceTextarea
          testId={APPLY.otherIncome} micTestId="apply-other-income-mic"
          value={data.other_income || ""} onChange={(v) => onPatch("other_income", v)}
          rows={2} maxChars={1500}
          placeholder="Benefits, child support received, housing subsidies, side work, etc."
        />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Expected monthly household income ($)">
          <MoneyInput testId={APPLY.householdMonthlyIncome} placeholder="Example: 2400.00"
            value={data.household_monthly_income || ""} onChange={(v) => onPatch("household_monthly_income", v)} />
        </Field>
        <Field label="Can you pay the security deposit?" hint="The deposit is negotiable and will be discussed before lease signing.">
          <select className="p2p-input" data-testid={APPLY.canPayDeposit}
            value={data.can_pay_deposit || ""} onChange={(e) => onPatch("can_pay_deposit", e.target.value)}>
            <option value="">Select…</option>
            <option value="yes">Yes</option>
            <option value="partial">Partially / I can make a plan</option>
            <option value="no">Not at this time</option>
          </select>
        </Field>
        <Field label="When would the security deposit be available?">
          <input type="date" className="p2p-input" data-testid={APPLY.depositAvailableDate}
            value={data.deposit_available_date || ""} onChange={(e) => onPatch("deposit_available_date", e.target.value)} />
        </Field>
      </div>
      <label className="flex items-start gap-3">
        <input type="checkbox" className="mt-1" data-testid={APPLY.authorizeContact}
          checked={!!data.authorize_contact} onChange={(e) => onPatch("authorize_contact", e.target.checked)} />
        <span className="text-sm" style={{ color: "#0B2D59" }}>
          I authorize the housing provider to contact my employer or income-verification source about the
          information I have provided.
        </span>
      </label>

      <div>
        <div className="font-serif text-lg mb-2" style={{ color: "#0B2D59" }}>Upload supporting documents</div>
        <p className="p2p-help mb-3">
          Upload what you have. If something is missing, we may follow up instead of automatically
          closing your application.
        </p>
        <FileUploader returnToken={token} documents={documents} onUploaded={onUploaded}
          onReplaced={onReplaced} onRemoved={onRemoved} testId={APPLY.uploadDocs} />
      </div>
    </div>
  );
}

function ReferencesStep({ refs, onChange }) {
  const add = () => onChange([...(refs || []), { name: "", phone: "", email: "", relationship: "", years_known: "", best_time: "", type: "professional", consent: false }]);
  const set = (i, k, v) => onChange(refs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const rm = (i) => onChange(refs.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-5">
      <SectionHeader title="Step 4 — References"
        sub="References help us verify information and understand your housing readiness through objective communication. At least two are required — they can be employers, landlords, professionals, community members, or friends." />
      {(refs || []).map((r, i) => (
        <div key={i} className="p-5 rounded-xl" style={{ background: "#F4F8FB", border: "1px solid #E2E8F0" }}>
          <div className="flex justify-between items-center mb-3">
            <div className="font-serif" style={{ color: "#0B2D59" }}>Reference {i + 1}</div>
            {refs.length > 0 && (
              <button className="text-sm inline-flex items-center gap-1" style={{ color: "#B91C1C" }} onClick={() => rm(i)}>
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <VoiceInput placeholder="Full name" testId={`apply-ref-name-${i}`} micTestId={`apply-ref-name-mic-${i}`} value={r.name || ""} onChange={(v) => set(i, "name", v)} />
            <input className="p2p-input" placeholder="Phone number" data-testid={`apply-ref-phone-${i}`} value={r.phone || ""} onChange={(e) => set(i, "phone", e.target.value)} />
            <input className="p2p-input" placeholder="Email" data-testid={`apply-ref-email-${i}`} value={r.email || ""} onChange={(e) => set(i, "email", e.target.value)} />
            <VoiceInput placeholder="Relationship to you" testId={`apply-ref-rel-${i}`} micTestId={`apply-ref-rel-mic-${i}`} value={r.relationship || ""} onChange={(v) => set(i, "relationship", v)} />
            <VoiceInput placeholder="How long have they known you?" testId={`apply-ref-years-${i}`} micTestId={`apply-ref-years-mic-${i}`} value={r.years_known || ""} onChange={(v) => set(i, "years_known", v)} />
            <VoiceInput placeholder="Best time to contact them" testId={`apply-ref-best-${i}`} micTestId={`apply-ref-best-mic-${i}`} value={r.best_time || ""} onChange={(v) => set(i, "best_time", v)} />
            <select className="p2p-input" value={r.type || "professional"} onChange={(e) => set(i, "type", e.target.value)}>
              {["employer", "landlord", "professional", "community", "friend", "other"].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <label className="flex items-start gap-3 mt-3" data-testid={APPLY.refConsent}>
            <input type="checkbox" className="mt-1" checked={!!r.consent} onChange={(e) => set(i, "consent", e.target.checked)} />
            <span className="text-sm" style={{ color: "#0B2D59" }}>
              I consent to the housing provider contacting this reference.
            </span>
          </label>
        </div>
      ))}
      <button type="button" className="p2p-btn-outline inline-flex items-center gap-2" data-testid={APPLY.addReference} onClick={add}>
        <Plus className="w-4 h-4" /> Add a reference
      </button>
    </div>
  );
}

function StoryStep({ story, token, onPatch }) {
  const [followups, setFollowups] = useState({});
  const [loadingFu, setLoadingFu] = useState({});
  const [cleanups, setCleanups] = useState({});
  const [loadingCu, setLoadingCu] = useState({});

  const runCleanup = async (i) => {
    const key = `q${i}`;
    if (story[`${key}_pna`]) return;
    const ans = (story[key] || "").trim();
    if (ans.length < 15) {
      toast.info("Write your answer first, then use the cleanup tool.");
      return;
    }
    setLoadingCu((s) => ({ ...s, [key]: true }));
    try {
      const { data } = await api.post(`/applications/${token}/story-cleanup`, { text: ans });
      setCleanups((s) => ({ ...s, [key]: { original: data.original, suggestion: data.suggestion, editable: false } }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail
        : "The cleanup tool is not available right now. Your answer is safe — you can keep it as written or try again later.");
    } finally {
      setLoadingCu((s) => ({ ...s, [key]: false }));
    }
  };

  const askFollowup = async (i) => {
    const key = `q${i}`;
    const ans = story[key] || "";
    if (story[`${key}_pna`]) return;
    if (ans.trim().length < 15) {
      toast.info("Add a bit more to your answer first, then we can suggest a clarifying question.");
      return;
    }
    setLoadingFu((s) => ({ ...s, [key]: true }));
    try {
      const { data } = await api.post(`/applications/${token}/ai-followup`, {
        question_id: key, question_text: STORY_QUESTIONS[i], applicant_answer: ans,
      });
      if (data.followup) setFollowups((s) => ({ ...s, [key]: data.followup }));
      else toast.info("No clarifying question needed — your answer is clear.");
    } catch (e) { /* ignore */ } finally {
      setLoadingFu((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader title="Your Story"
        sub="This is your space. Share what feels helpful. You are not required to explain your whole life." />
      <div className="encourage text-sm" data-testid="story-intro">
        This section is optional. It gives you space to share anything you want us to understand about
        your goals, strengths, barriers, and what support may help you if you move forward. You do not
        have to share private medical, legal, family, or personal details unless you choose to. Your
        answers are not used to score, rank, or judge your application.
      </div>
      {STORY_QUESTIONS.map((q, i) => {
        const key = `q${i}`;
        const pna = !!story[`${key}_pna`];
        const fu = followups[key];
        return (
          <div key={i} className="p-5 rounded-xl" style={{ background: "#F4F8FB", border: "1px solid #E2E8F0" }}>
            <div className="font-serif text-lg mb-3" style={{ color: "#0B2D59" }}>{i + 1}. {q}</div>
            {STORY_HELP[i] && (
              <p className="text-sm mb-3 leading-relaxed" data-testid={`story-help-${i}`} style={{ color: "#475569" }}>
                {STORY_HELP[i]}
              </p>
            )}
            <VoiceTextarea
              value={pna ? "" : (story[key] || "")}
              onChange={(v) => onPatch(key, v)}
              placeholder="Take your time."
              testId={APPLY.storyQ(i)}
              micTestId={APPLY.micBtn(i)}
              disabled={pna}
              rows={4}
            />
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm" style={{ color: "#475569" }}>
                <input type="checkbox" data-testid={APPLY.pnaBtn(i)}
                  checked={pna} onChange={(e) => onPatch(`${key}_pna`, e.target.checked)} />
                Prefer not to answer
              </label>
              {!pna && (
                <button type="button" className="text-sm inline-flex items-center gap-2 underline" data-testid={APPLY.followupBtn(i)}
                  onClick={() => askFollowup(i)} disabled={loadingFu[key]} style={{ color: "#0B2D59" }}>
                  <Sparkles className="w-3 h-3" /> {loadingFu[key] ? "Thinking…" : "Ask for a clarifying question"}
                </button>
              )}
              {!pna && (story[key] || "").trim().length > 0 && (
                <button type="button" className="text-sm inline-flex items-center gap-2 underline" data-testid={`story-cleanup-btn-${i}`}
                  onClick={() => runCleanup(i)} disabled={loadingCu[key]} style={{ color: "#0B2D59" }}>
                  <Wand2 className="w-3 h-3" /> {loadingCu[key] ? "Cleaning up…" : "Clean up my wording"}
                </button>
              )}
            </div>
            {!pna && (story[key] || "").trim().length > 0 && !cleanups[key] && (
              <p className="text-xs mt-2" style={{ color: "#475569" }}>
                This optional tool can help clean up wording while keeping your meaning the same. You control what gets submitted.
              </p>
            )}
            {cleanups[key] && !pna && (
              <div className="mt-3 p-4 rounded-lg space-y-3" data-testid={`cleanup-panel-${i}`}
                style={{ background: "#fff", border: "1px solid #00A7B5" }}>
                <div className="text-sm font-semibold flex items-center gap-2" style={{ color: "#0B2D59" }}>
                  <Wand2 className="w-4 h-4" style={{ color: "#00A7B5" }} /> Wording suggestion — you choose what gets saved
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#475569" }}>Your original answer</div>
                  <div className="text-sm mt-1 p-2 rounded" style={{ color: "#0B2D59", background: "#F4F8FB" }}>{cleanups[key].original}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#475569" }}>
                    Suggested cleaned-up version {cleanups[key].editable && "(you can edit it now)"}
                  </div>
                  <textarea rows={4} className="p2p-input mt-1" data-testid={`cleanup-suggestion-${i}`}
                    value={cleanups[key].suggestion}
                    readOnly={!cleanups[key].editable}
                    onChange={(e) => setCleanups((s) => ({ ...s, [key]: { ...s[key], suggestion: e.target.value } }))} />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="p2p-btn-primary text-sm" style={{ padding: "0.45rem 1rem" }}
                    data-testid={`cleanup-use-btn-${i}`}
                    onClick={() => {
                      onPatch(key, cleanups[key].suggestion);
                      setCleanups((s) => ({ ...s, [key]: null }));
                      toast.success("Updated with your chosen wording.");
                    }}>
                    Use cleaned-up version
                  </button>
                  {!cleanups[key].editable && (
                    <button type="button" className="p2p-btn-outline text-sm" style={{ padding: "0.45rem 1rem" }}
                      data-testid={`cleanup-edit-btn-${i}`}
                      onClick={() => setCleanups((s) => ({ ...s, [key]: { ...s[key], editable: true } }))}>
                      Edit before saving
                    </button>
                  )}
                  <button type="button" className="p2p-btn-outline text-sm" style={{ padding: "0.45rem 1rem" }}
                    data-testid={`cleanup-keep-btn-${i}`}
                    onClick={() => setCleanups((s) => ({ ...s, [key]: null }))}>
                    Keep my original version
                  </button>
                </div>
              </div>
            )}
            {fu && !pna && (
              <div className="mt-3 p-3 rounded-lg flex items-start gap-3" style={{ background: "#fff", border: "1px solid #00A7B5" }}>
                <Sparkles className="w-4 h-4 mt-0.5" style={{ color: "#00A7B5" }} />
                <div className="flex-1">
                  <div className="text-sm font-semibold" style={{ color: "#0B2D59" }}>Optional follow-up</div>
                  <div className="text-sm" style={{ color: "#0B2D59" }}>{fu}</div>
                  <textarea rows={2} className="p2p-input mt-2"
                    value={story[`${key}_followup`] || ""}
                    onChange={(e) => onPatch(`${key}_followup`, e.target.value)}
                    placeholder="Optional. You can skip this." />
                </div>
                <button onClick={() => setFollowups((s) => ({ ...s, [key]: null }))} aria-label="dismiss">
                  <X className="w-4 h-4" style={{ color: "#475569" }} />
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LeaseStep({ ack, onPatch }) {
  return (
    <div className="space-y-5">
      <SectionHeader title="Step 6 — Lease expectations"
        sub="We want you to succeed here. These are the everyday expectations that come with renting this home." />
      <div className="encourage text-sm">
        These are everyday expectations of renting this home. Please review and acknowledge each one.
      </div>
      <ul className="space-y-3">
        {LEASE_ITEMS.map(([k, label]) => (
          <li key={k} className="flex items-start gap-3 p-4 rounded-xl" style={{ background: "#F4F8FB", border: "1px solid #E2E8F0" }}>
            <input type="checkbox" className="mt-1" data-testid={APPLY.ackChk(k)}
              checked={!!ack[k]} onChange={(e) => onPatch(k, e.target.checked)} />
            <span style={{ color: "#0B2D59" }}>{label}</span>
          </li>
        ))}
      </ul>
      <p className="p2p-help">
        A call to the police does not automatically cause eviction. All lease enforcement follows applicable Nebraska law.
      </p>
      <p className="p2p-help" data-testid="lease-review-note">
        These acknowledgments are a plain-language summary, not the lease itself and not legal advice.
        Please read the full lease carefully before signing it — you are welcome to ask questions or
        have someone you trust review it with you.
      </p>
    </div>
  );
}

function ReviewStep({ doc, signature, onPatch, onJump }) {
  const data = doc.data || {};
  return (
    <div className="space-y-6">
      <SectionHeader title="Step 7 — Review & sign"
        sub="Please review your answers before submitting. You control what you submit. A real person will review your application." />

      <ReviewBlock title="Contact" step={1} onEdit={() => onJump(1)}>
        <Pairs obj={data.contact || {}} />
      </ReviewBlock>
      <ReviewBlock title="Current housing" step={2} onEdit={() => onJump(2)}>
        <Pairs obj={data.housing || {}} />
      </ReviewBlock>
      <ReviewBlock title="Income & documents" step={3} onEdit={() => onJump(3)}>
        <Pairs obj={data.income || {}} />
        <div className="text-xs mt-2" style={{ color: "#475569" }}>
          Documents uploaded: {(doc.documents || []).length}
        </div>
      </ReviewBlock>
      <ReviewBlock title={`References (${(data.references || []).length})`} step={4} onEdit={() => onJump(4)}>
        {(data.references || []).map((r, i) => (
          <div key={i} className="text-sm" style={{ color: "#0B2D59" }}>
            {r.name} — {r.relationship} ({r.type})
          </div>
        ))}
      </ReviewBlock>
      <ReviewBlock title="Your Story" step={5} onEdit={() => onJump(5)}>
        <div className="text-sm" style={{ color: "#475569" }}>
          Voluntary. Your answers are not used to score, rank, or judge your application, and they stay
          with this housing provider only.
        </div>
      </ReviewBlock>
      <ReviewBlock title="Lease acknowledgments" step={6} onEdit={() => onJump(6)}>
        <Pairs obj={data.lease_ack || {}} />
      </ReviewBlock>

      <div className="p2p-card p-5" style={{ borderColor: "#00A7B5" }}>
        <div className="font-serif text-lg" style={{ color: "#0B2D59" }}>Consent & signature</div>
        <div className="space-y-3 mt-3">
          <label className="flex items-start gap-3 text-sm" style={{ color: "#0B2D59" }}>
            <input type="checkbox" className="mt-1" data-testid={APPLY.certify}
              checked={!!signature.certified} onChange={(e) => onPatch("certified", e.target.checked)} />
            I certify the information I have provided is true and complete to the best of my knowledge.
          </label>
          <label className="flex items-start gap-3 text-sm" style={{ color: "#0B2D59" }}>
            <input type="checkbox" className="mt-1" data-testid={APPLY.consentVerify}
              checked={!!signature.consent_verify} onChange={(e) => onPatch("consent_verify", e.target.checked)} />
            I consent to the housing provider verifying my employment, income, rental history, and references.
          </label>
          <label className="flex items-start gap-3 text-sm" style={{ color: "#0B2D59" }}>
            <input type="checkbox" className="mt-1" data-testid={APPLY.consentAI}
              checked={!!signature.consent_ai} onChange={(e) => onPatch("consent_ai", e.target.checked)} />
            I understand that artificial intelligence may help organize and summarize the information I provide. AI will not make the final rental decision. A person will review every completed application.
          </label>
          <div>
            <span className="p2p-label">Type or speak your full legal name as signature</span>
            <VoiceInput testId={APPLY.signatureName} micTestId="apply-signature-name-mic"
              value={signature.signature_name || ""} onChange={(v) => onPatch("signature_name", v)} />
            <div className="text-xs mt-1" style={{ color: "#475569" }}>Date: {new Date().toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ title, sub }) {
  return (
    <div>
      <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: "#0B2D59" }}>{title}</h2>
      {sub && <p className="mt-2" style={{ color: "#1F2937" }}>{sub}</p>}
    </div>
  );
}

function ReviewBlock({ title, step, onEdit, children }) {
  return (
    <div className="p-4 rounded-xl" style={{ background: "#F4F8FB", border: "1px solid #E2E8F0" }}>
      <div className="flex items-center justify-between">
        <div className="font-serif text-lg" style={{ color: "#0B2D59" }}>{title}</div>
        <button onClick={onEdit} className="text-sm underline" style={{ color: "#0B2D59" }}>Edit</button>
      </div>
      <div className="mt-2 text-sm" style={{ color: "#0B2D59" }}>{children}</div>
    </div>
  );
}

function Pairs({ obj }) {
  const entries = Object.entries(obj || {}).filter(([k, v]) => v !== "" && v !== null && v !== undefined);
  if (!entries.length) return <div className="text-sm" style={{ color: "#475569" }}>— not provided —</div>;
  return (
    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-2">
          <dt className="font-semibold capitalize" style={{ color: "#475569" }}>{k.replaceAll("_", " ")}:</dt>
          <dd style={{ color: "#0B2D59" }}>{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}
